export const DROFUS_DATE_PRESET_OPTIONS = [
  { value: "week", label: "Siste uke" },
  { value: "month", label: "Siste måned" },
  { value: "quarter", label: "Siste 3 måneder" },
  { value: "custom", label: "Egendefinert" },
] as const;

export type DrofusDatePreset = typeof DROFUS_DATE_PRESET_OPTIONS[number]["value"];
export interface DrofusDateRange {
  datePreset: DrofusDatePreset;
  dateFrom: string;
  dateTo: string;
}
export const DROFUS_DATE_RANGE_STORAGE_KEY = "multiplacer.drofusChanges.dateRange";

const formatDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export function getDrofusDateRange(datePreset: Exclude<DrofusDatePreset, "custom">, now = new Date()): DrofusDateRange {
  const from = new Date(now);
  if (datePreset === "week") {
    from.setDate(from.getDate() - 7);
  } else {
    // Clamp month ends: March 31 minus one month is February 28/29.
    from.setDate(1);
    from.setMonth(from.getMonth() - (datePreset === "month" ? 1 : 3));
    const lastDay = new Date(from.getFullYear(), from.getMonth() + 1, 0).getDate();
    from.setDate(Math.min(now.getDate(), lastDay));
  }
  return { datePreset, dateFrom: formatDate(from), dateTo: formatDate(now) };
}

const isDateInput = (value: unknown): value is string => {
  if (value === "") return true;
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T12:00:00`);
  return Number.isFinite(date.getTime()) && formatDate(date) === value;
};

export function readDrofusDateRange(now = new Date()): DrofusDateRange {
  try {
    const saved = JSON.parse(localStorage.getItem(DROFUS_DATE_RANGE_STORAGE_KEY) ?? "null");
    if (saved?.datePreset === "week" || saved?.datePreset === "month" || saved?.datePreset === "quarter") {
      return getDrofusDateRange(saved.datePreset, now);
    }
    if (saved?.datePreset === "custom" && isDateInput(saved.dateFrom) && isDateInput(saved.dateTo)) {
      return { datePreset: "custom", dateFrom: saved.dateFrom, dateTo: saved.dateTo };
    }
  } catch { /* Storage is optional. */ }
  return getDrofusDateRange("week", now);
}

export function saveDrofusDateRange(range: DrofusDateRange): void {
  try {
    const { datePreset, dateFrom, dateTo } = range;
    localStorage.setItem(DROFUS_DATE_RANGE_STORAGE_KEY, JSON.stringify(
      datePreset === "custom" ? { datePreset, dateFrom, dateTo } : { datePreset }
    ));
  } catch { /* Storage is optional. */ }
}
